import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { Loader2, X } from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import CustomersNoticeFmtBar from '../../users-customers/components/CustomersNoticeFmtBar';
import CustomersNoticeOryaCompose from '../../users-customers/components/CustomersNoticeOryaCompose';
import { useComposerVoiceRecorder } from './ComposerVoiceRecorder';
import '../../users-customers/components/CustomersNoticeFmtBar.css';
import './BoardChatPreAtendimentoCloseModal.css';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
}

function caretCoords(textarea) {
  if (!textarea) return null;
  const r = textarea.getBoundingClientRect();
  return { top: r.top + 8, left: r.left + 12 };
}

function selectionBarPosition(textarea) {
  if (!textarea) return null;
  const r = textarea.getBoundingClientRect();
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  if (end <= start) return null;
  // Approx: bar above the field, centered on selection range ratio
  const len = Math.max(1, String(textarea.value || '').length);
  const mid = (start + end) / 2 / len;
  const left = Math.min(
    Math.max(8, r.left + mid * r.width - 90),
    window.innerWidth - 200
  );
  const top = Math.max(8, r.top - 40);
  return { top, left };
}

/**
 * Nota obrigatória antes de abrir o fluxo de encaminhar (técnico/agenda).
 * Inclui assistência Orya ( "/" e seleção → Melhorar / Edit ) e fala→texto.
 */
export function BoardChatPreAtendimentoForwardNoteModal({ open, osId, onClose, onContinue }) {
  const textareaRef = useRef(null);
  const noteBodyRef = useRef(null);
  const selRef = useRef({ start: 0, end: 0, text: '' });
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [oryaCompose, setOryaCompose] = useState(null);
  const [fmt, setFmt] = useState(null);
  const fmtOpenRef = useRef(false);
  fmtOpenRef.current = Boolean(fmt);
  const [voiceAnchor, setVoiceAnchor] = useState(null);
  const [voiceError, setVoiceError] = useState('');

  const voiceRecorder = useComposerVoiceRecorder({});
  const voiceRecRef = useRef(voiceRecorder);
  voiceRecRef.current = voiceRecorder;
  const voiceActive = Boolean(voiceRecorder.recording);

  useEffect(() => {
    if (!open) return;
    setText('');
    setError('');
    setSaving(false);
    setOryaCompose(null);
    setFmt(null);
    setVoiceAnchor(null);
    setVoiceError('');
  }, [open, osId]);

  const stopVoice = useCallback(async () => {
    const rec = voiceRecRef.current;
    if (rec?.recording) await rec.cancel();
    setVoiceAnchor(null);
    setVoiceError('');
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    return () => {
      stopVoice();
    };
  }, [open, stopVoice]);

  // Impede ESC de fechar este modal (só Cancelar / X).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (oryaCompose) {
        setOryaCompose(null);
        return;
      }
      if (fmt) {
        setFmt(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, oryaCompose, fmt]);

  const syncSelection = useCallback(() => {
    const el = textareaRef.current;
    if (!el || oryaCompose) {
      if (oryaCompose) setFmt(null);
      return;
    }
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (end <= start) {
      if (!fmtOpenRef.current) setFmt(null);
      return;
    }
    const selectedText = String(el.value || '').slice(start, end);
    if (!selectedText.trim()) {
      if (!fmtOpenRef.current) setFmt(null);
      return;
    }
    selRef.current = { start, end, text: selectedText };
    const pos = selectionBarPosition(el);
    if (!pos) {
      if (!fmtOpenRef.current) setFmt(null);
      return;
    }
    setFmt({ ...pos, selectedText });
  }, [oryaCompose]);

  const openOryaCompose = useCallback(() => {
    const el = textareaRef.current;
    const pos = caretCoords(el) || { top: 120, left: 120 };
    setFmt(null);
    setOryaCompose(pos);
  }, []);

  const handleTextChange = (e) => {
    const el = e.target;
    const next = el.value;
    const caret = el.selectionStart ?? next.length;
    const before = next.slice(0, caret);
    // "/" sozinho no início da linha (como no editor de avisos)
    const lineStart = before.lastIndexOf('\n') + 1;
    const lineBeforeCaret = before.slice(lineStart);
    if (lineBeforeCaret === '/' && !oryaCompose) {
      const cleaned = next.slice(0, lineStart) + next.slice(caret);
      setText(cleaned);
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = lineStart;
          textareaRef.current.selectionEnd = lineStart;
        }
        openOryaCompose();
      });
      return;
    }
    setText(next);
    if (!fmtOpenRef.current) setFmt(null);
  };

  const resolveSelRange = useCallback((value) => {
    let { start, end, text: saved } = selRef.current || {};
    start = Number(start) || 0;
    end = Number(end) || 0;
    const v = String(value || '');
    if (end > start && end <= v.length && (!saved || v.slice(start, end) === saved)) {
      return { start, end };
    }
    if (saved) {
      const idx = v.indexOf(saved);
      if (idx >= 0) return { start: idx, end: idx + saved.length };
    }
    const caret = Math.min(Math.max(start, 0), v.length);
    return { start: caret, end: caret };
  }, []);

  const replaceSelection = useCallback(
    (replacement) => {
      const el = textareaRef.current;
      const value = String(el?.value ?? text);
      const { start, end } = resolveSelRange(value);
      const insert = String(replacement || '');
      const next = value.slice(0, start) + insert + value.slice(end);
      setText(next);
      setFmt(null);
      selRef.current = { start: 0, end: 0, text: '' };
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        const pos = start + insert.length;
        textareaRef.current.focus();
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
      });
    },
    [text, resolveSelRange]
  );

  const insertBelowSelection = useCallback(
    (suggestion) => {
      const base = String(selRef.current?.text || fmt?.selectedText || '').trimEnd();
      const add = String(suggestion || '');
      replaceSelection(base ? `${base}\n${add}` : add);
    },
    [fmt?.selectedText, replaceSelection]
  );

  const insertOryaText = useCallback((content) => {
    const el = textareaRef.current;
    const value = String(el?.value ?? text);
    const caret = el?.selectionStart ?? value.length;
    const insert = String(content || '');
    const next = value.slice(0, caret) + insert + value.slice(caret);
    setText(next);
    setOryaCompose(null);
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const pos = caret + insert.length;
      textareaRef.current.focus();
      textareaRef.current.selectionStart = pos;
      textareaRef.current.selectionEnd = pos;
    });
  }, [text]);

  const handleVoiceClick = useCallback(async () => {
    setVoiceError('');
    const rec = voiceRecRef.current;
    if (rec.recording) {
      rec.toggle();
      setVoiceAnchor(null);
      return;
    }
    const wrap = noteBodyRef.current;
    const r = (wrap || textareaRef.current)?.getBoundingClientRect?.();
    if (r) {
      setVoiceAnchor({
        top: r.bottom + 6,
        left: Math.max(12, r.right - 268),
      });
    }
    await rec.toggle();
  }, []);

  const handleTranscribe = async () => {
    setVoiceError('');
    try {
      const rec = voiceRecRef.current;
      if (!rec.speechSupported) {
        await rec.cancel();
        setVoiceError('Transcrição não suportada neste navegador.');
        return;
      }
      const transcribed = String((await rec.finishTranscript()) || '').trim();
      if (!transcribed) {
        setVoiceError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setText((prev) => {
        const cur = String(prev || '').trim();
        return cur ? `${cur} ${transcribed}` : transcribed;
      });
      setVoiceAnchor(null);
    } catch (e) {
      setVoiceError(e?.message || 'Não foi possível obter o texto desta gravação.');
    }
  };

  if (!open || !osId) return null;

  const canSend = text.trim().length > 0 && !saving;

  const handleContinue = async () => {
    const obs = text.trim();
    if (!obs || saving) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(osId)}/observacoes`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ obs }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Não foi possível salvar a anotação.');
      }
      onContinue?.({ osId, note: obs });
    } catch (e) {
      setError(e.message || 'Falha ao salvar a anotação.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="bch-pre-close-overlay bch-pre-forward-note-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Anotação para encaminhar OS ${osId}`}
    >
      <button type="button" className="bch-pre-close-backdrop" aria-label="Fundo" tabIndex={-1} />
      <div className="bch-pre-close-panel bch-pre-forward-note-panel">
        <header className="bch-pre-close-head bch-pre-forward-note-head">
          <div className="bch-pre-close-head-main">
            <span className="bch-pre-close-os">OS {osId}</span>
            <span className="bch-pre-close-sub">Anotação antes de encaminhar</span>
          </div>
          <button type="button" className="bch-pre-close-x" onClick={onClose} aria-label="Cancelar">
            <IconX size={16} stroke={1.8} />
          </button>
        </header>

        <div className="bch-pre-forward-note-body">
          <p className="bch-pre-forward-note-hint">
            Registre o que foi feito no pré-atendimento. Digite <kbd>/</kbd> para a Orya ou selecione
            o texto para melhorar.
          </p>
          <div className="bch-pre-forward-note-input-wrap" ref={noteBodyRef}>
            <textarea
              ref={textareaRef}
              className="bch-pre-forward-note-input"
              value={text}
              onChange={handleTextChange}
              onSelect={syncSelection}
              onMouseUp={syncSelection}
              onKeyUp={syncSelection}
              placeholder="Digite uma anotação… ( / para Orya )"
              rows={12}
              disabled={saving}
              aria-label="Anotação da OS"
              autoFocus
            />
            <div className="bch-pre-close-laudo-tools">
              <button
                type="button"
                className={`bch-pre-close-voice-btn${voiceActive ? ' is-recording' : ''}`}
                aria-label="Fala para texto"
                onClick={handleVoiceClick}
                disabled={saving}
              >
                <span className="bch-pre-close-voice-tip" aria-hidden>
                  Grave em voz e tenha o texto transcrito aqui
                </span>
                <svg
                  className="bch-pre-close-voice-ico"
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="6"
                    y="2"
                    width="4"
                    height="7"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M4 8a4 4 0 008 0M8 12v2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <span>Fala para texto</span>
              </button>
            </div>
          </div>
          {error ? <p className="bch-pre-close-error">{error}</p> : null}
        </div>

        <footer className="bch-pre-close-foot bch-pre-forward-note-foot">
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={handleContinue}
            disabled={!canSend}
          >
            {saving ? <Loader2 size={14} className="bch-pre-close-spin" /> : null}
            Continuar
          </button>
        </footer>
      </div>

      {oryaCompose ? (
        <CustomersNoticeOryaCompose
          position={oryaCompose}
          noticeTitle={`Anotação OS ${osId}`}
          keepOpenSelector=".bch-pre-forward-note-panel, .bch-pre-forward-note-input"
          onInsert={insertOryaText}
          onClose={() => setOryaCompose(null)}
        />
      ) : null}

      {fmt && !oryaCompose ? (
        <CustomersNoticeFmtBar
          aiOnly
          position={{ top: fmt.top, left: fmt.left }}
          selectedText={fmt.selectedText}
          keepOpenSelector=".bch-pre-forward-note-panel, .bch-pre-forward-note-input"
          onReplaceSelection={replaceSelection}
          onInsertBelow={insertBelowSelection}
          onClose={() => setFmt(null)}
        />
      ) : null}

      {voiceActive || voiceError ? (
        <div
          className="bch-pre-close-voice-pop"
          style={
            voiceAnchor
              ? { top: `${voiceAnchor.top}px`, left: `${voiceAnchor.left}px` }
              : undefined
          }
          role="status"
          aria-live="polite"
        >
          {voiceActive ? (
            <>
              <span
                className={`bch-pre-close-voice-pulse${voiceRecorder.busy ? ' is-busy' : ''}`}
                aria-hidden
              />
              <span className="bch-pre-close-voice-timer">
                {`${Math.floor((voiceRecorder.elapsedSec || 0) / 60)}:${String(
                  (voiceRecorder.elapsedSec || 0) % 60
                ).padStart(2, '0')}`}
              </span>
              <span className="bch-pre-close-voice-label">
                {voiceRecorder.busy ? 'Processando…' : 'Gravando…'}
              </span>
              <button
                type="button"
                className="bch-pre-close-voice-transcribe"
                disabled={voiceRecorder.busy}
                onClick={handleTranscribe}
              >
                Transcrever
              </button>
              <button
                type="button"
                className="bch-pre-close-voice-cancel"
                disabled={voiceRecorder.busy}
                aria-label="Cancelar gravação"
                onClick={stopVoice}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </>
          ) : null}
          {voiceError ? <div className="bch-pre-close-voice-error">{voiceError}</div> : null}
        </div>
      ) : null}
    </div>,
    document.body
  );
}
